import { useState, useEffect } from "react";
// @mui
import {
  Avatar,
  Box,
  Typography,
  Stack,
  Button,
  Divider,
  Card,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// components
import Iconify from "../../../components/Iconify";
import SvgIconStyle from "../../../components/SvgIconStyle";
import Label from "../../../components/Label";
import { FreeCoffeeChatDialogButton } from '../../scout';
// hooks
import useAuth from "../../../hooks/useAuth";
import useResponsive from "../../../hooks/useResponsive";
// swr
import useSWR from 'swr';
// utils
import { fKrKSTFile, fKrKSTShort } from '../../../utils/formatTime';
import axios from '../../../utils/axios';
import axiosSWRFetcher from '../../../utils/swrFetcher';

// ----------------------------------------------------------------------

export default function MessagePanel({
  coffeeChat,
  isOpenSendMessage, openSendMessage, closeSendMessage,
  isOpenReadMessage, selectedMessage, openReadMessage, closeReadMessage,
}) {
  const { user } = useAuth();
  const companyId = user?.employee?.company?.[0].id;
  const { data: messages, isValidating, mutate } = useSWR(companyId && coffeeChat ? `/api/company/${user?.employee?.company?.[0].id}/fliers/coffee/${coffeeChat.id}/message/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const smUp = useResponsive('up', 'sm');

  const paidPlan = user?.employee?.company?.[0]?.scout_plan === 'enterprise' || user?.employee?.company?.[0]?.scout_plan === 'pro';

  const sendMessage = async () => {
    setIsSending(true);
    await axios.post(`/api/company/${companyId}/fliers/coffee/${coffeeChat.id}/message/`, {
      message,
    })
    setIsSending(false);
    mutate();
    closeSendMessage();
  };

  const messageLabelColor = (label) => {
    switch (label) {
      case "파트타임 제안":
        return "info";
      default:
        return "default";
    }
  }

  useEffect(() => {
    if (isOpenSendMessage) {
      setMessage("");
      setIsSending(false);
    }
  }, [isOpenSendMessage]);

  const noMessage = messages?.length === 0;

  if (!paidPlan) {
    return (
      <Stack sx={{ justifyContent: "center", alignItems: "center" }}>
        <Typography variant="h6">
          플랜을 업그레이드 하면,
        </Typography>
        <Typography variant="h6">
          크루와 메시지를 주고받을 수 있어요.
        </Typography>
        <FreeCoffeeChatDialogButton buttonTitle="플랜 업그레이드" sx={{ mt: 2, alignItems: "center" }} buttonSource="message" />
      </Stack>
    )
  }

  if (isOpenSendMessage) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 2 }}>
          <IconButton color="inherit" edge="start" onClick={closeSendMessage}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography variant="h5" gutterBottom>
          크루에게 메시지 보내기
        </Typography>
        <Typography variant="body2" paragraph>
          메시지를 보내면 크루에게 바로 전달됩니다.
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={8}
          placeholder="전달할 메시지를 입력해주세요."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
        />
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
          <LoadingButton
            color="primary"
            variant="contained"
            size="large"
            onClick={sendMessage}
            sx={{ px: 6 }}
            disabled={!message}
            loading={isSending}
          >
            보내기
          </LoadingButton>
        </Stack>
      </>
    )
  }

  if (isOpenReadMessage) {
    return (
      <>
        {/* < 버튼 표시 */}
        <Box sx={{ display: "flex", mb: 2 }}>
          <IconButton color="inherit" edge="start" onClick={closeReadMessage}>
            <SvgIconStyle src={`/icons/chevron-left.svg`} sx={{ width: 32, height: 32, color: "text.secondary" }} />
          </IconButton>
        </Box>
        <Typography variant="h5" gutterBottom>
          메시지 내용 확인하기
        </Typography>
        <Stack direction="row" alignItems={"center"} spacing={.5}>
          <Typography variant="subtitle2" color="text.disabled">
            {selectedMessage.sent_crew ? "받은 메시지" : selectedMessage.sent_company ? "보낸 메시지" : "DIO"} •
          </Typography>
          <Typography variant="body2" sx={{ pr: 0.5, color: "text.secondary" }}>
            {smUp ? fKrKSTFile(selectedMessage.sent_at) : fKrKSTShort(selectedMessage.sent_at)}
          </Typography>
          {selectedMessage.label && (
            <Label color={messageLabelColor(selectedMessage.label)}>
              {selectedMessage.label}
            </Label>
          )}
        </Stack>
        <Box
          sx={{
            mt: 2,
            p: 2.5,
            backgroundColor: "grey.200",
            borderRadius: 2,
          }}
        >
          <Typography variant="body1" whiteSpace={"pre-wrap"} sx={{ wordBreak: "break-all" }}>
            {selectedMessage.message}
          </Typography>
        </Box>
      </>
    )
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: { xs: 2, sm: 1 } }}>
        <Button
          color="inherit"
          size="medium"
          variant="outlined"
          startIcon={<Iconify icon="eva:email-fill" />}
          onClick={() => openSendMessage()}
        >
          메시지 보내기
        </Button>
      </Stack>
      {isValidating && !messages && (
        <Box sx={{ mt: 5, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress sx={{ color: 'secondary.main' }} />
        </Box>
      )}
      {!isValidating && noMessage && (
        <Card sx={{ mt: 2, p: 3 }}>
          <Typography sx={{ color: "text.secondary" }}>
            메시지가 없습니다.
          </Typography>
        </Card>
      )}
      <Stack spacing={2} divider={<Divider />}>
        {messages?.map((r, index) => (
          <Stack
            key={`r-${index}`}
            sx={{
              p: 1, width: 1, borderRadius: 1,
              '&:hover': {
                cursor: 'pointer',
                backgroundColor: "grey.200",
              },
              '&:active': {
                backgroundColor: "grey.300",
              }
            }}
            spacing={1}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openReadMessage(r);
            }}
          >
            <Typography variant="subtitle1" sx={{ color: r.sent_crew ? "text.primary" : "text.disabled", overflow: "hidden", textOverflow: "ellipsis" }} noWrap>
              {r.message}
            </Typography>
            <Stack direction="row" alignItems={"center"} spacing={.5}>
              <Typography variant="subtitle2" color="text.disabled">
                {r.sent_crew ? "받은 메시지" : r.sent_company ? "보낸 메시지" : "DIO"} •
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", pr: 0.5 }}>
                {smUp ? fKrKSTFile(r.sent_at) : fKrKSTShort(r.sent_at)}
              </Typography>
              {r.label && (
                <Label color={messageLabelColor(r.label)}>
                  {r.label}
                </Label>
              )}
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Box>
  )
};
